import React, { useState, useEffect } from 'react';
import {
  Button,
  Dropdown,
  Option,
  Spinner,
  Input,
  Radio,
  RadioGroup,
} from '@fluentui/react-components';
import {
  EditRegular,
  DeleteRegular,
} from '@fluentui/react-icons';
// import { ModuleService } from '../../../../services/module.service';
// import { AuthService } from '../../../../services/auth.service';
// import { TenderingService } from '../../../../services/tendering.service';
// import { TagService } from '../../../../services/tag.service';
// import { NoteService } from '../../../../services/note.service';
import { Module } from '../../../../models/module';
import { Tender, TenderAnswer, Tendering, Question } from '../../../../models/tendering';
import { UserSystemData } from '../../../../models/users';
import { changeTenderHideStatus, getTenderDocument, getTenderDocumentsByTenderingID, getTenderingDocument, updateTenderDocument } from '../../../../services/tenderingService';
import { createNote } from '../../../../services/noteService';
import ConfirmationDialog from '../../ConfirmationDialog';
import SuccessDialog from '../../SuccessDialog';
import ErrorDialog from '../../ErrorDialog';
import PasswordDialog from '../../PasswordDialog';
import ChangeAnswerDialog from '../../ChangeAnswerDialog';
import NewNote from '../../../TenderSettings/Dialogs/NewNote';
import { NoteCreateDTO } from '../../../../models/note';

interface SystemTendersProps {
  tenderingOptions: Module[];
  user: UserSystemData;
  systemPassword: string;
}

const SystemTenders: React.FC<SystemTendersProps> = ({
  tenderingOptions,
  user,
  systemPassword,
}) => {
  const [selectedTenderingID, setSelectedTenderingID] = useState<number>(tenderingOptions[0]?.moduleID);
  const [tendering, setTendering] = useState<Tendering | null>(null);
  const [selectedTender, setSelectedTender] = useState<Tender | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionAnswers, setQuestionAnswers] = useState<TenderAnswer[]>([]);
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [answerList, setAnswerList] = useState<TenderAnswer[]>([]);
  const [answerListUnfiltered, setAnswerListUnfiltered] = useState<TenderAnswer[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Dialog states
  const [changeAnswerOpen, setChangeAnswerOpen] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<TenderAnswer | null>(null);
  const [selectedQuestionText, setSelectedQuestionText] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState<any>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [tenderLogOpen, setTenderLogOpen] = useState(false);

  // const moduleService = new ModuleService();
  // const authService = new AuthService();
  // const tenderingService = new TenderingService();
  // const tagService = new TagService();
  // const noteService = new NoteService();

  const tenderingProperties = ['content'];
  const tenderProperties = ['content.tenderAnswers', 'content.tenderProperties.hidden', 'content.archivingUsers'];

  useEffect(() => {
    if (selectedTenderingID) {
      getTendering();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenderingID]);

  const getTendering = async (selectedTenderToReload?: Tender) => {
    setSelectedTender(null);
    setLoaded(false);
    setQuestions([]);

    try {
      const tenderingData = await getTenderingDocument(selectedTenderingID, tenderingProperties)
      setTendering(tenderingData);
      
      const allQuestions: Question[] = [];
      tenderingData.tenderingParts.forEach(p => {
        if (p.questions) {
          allQuestions.push(...p.questions);
        }
      });
      setQuestions(allQuestions);
      
      await getTenders(selectedTenderToReload);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
      setLoaded(false);
    }
  };

  const getTenders = async (selectedTenderToReload?: Tender) => {
    try {
      const tendersData = await getTenderDocumentsByTenderingID(selectedTenderingID, tenderProperties)
      setTenders(tendersData);
      
      if (selectedTenderToReload) {
        const reloadedTender = tendersData.find(t => t.tenderID === selectedTenderToReload.tenderID);
        if (reloadedTender) {
          setSelectedTender(reloadedTender);
          onTenderChange(reloadedTender);
        }
      }
      
      setLoaded(true);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
      setLoaded(false);
    }
  };

  const onTenderingChange = (moduleID: number) => {
    setSelectedTenderingID(moduleID);
  };

  const onTenderChange = (tender: Tender) => {
    setSelectedQuestion(null);
    setSelectedTender(tender);
    
    const unfilteredAnswers = [...(tender.tenderAnswers || [])];
    setAnswerListUnfiltered(unfilteredAnswers);

    if (!tendering) return;

    const tenderingQuestionsIDs = tendering.tenderingParts.flatMap(part => part.questions || []).map(q => q.questionID);
    const tenderQuestionIDs = unfilteredAnswers.map(a => a.questionID);
    const missingQuestionsIDs = tenderingQuestionsIDs.filter(id => !tenderQuestionIDs.includes(id));

    if (missingQuestionsIDs.length > 0) {
      missingQuestionsIDs.forEach(elem => {
        const question = getQuestionByID(elem);
        const newAnswer: TenderAnswer = {
          questionID: elem,
          tenderID: tender.tenderID,
          value: null,
          type: convertQuestionType(question?.questionType || 'string'),
        };
        unfilteredAnswers.push(newAnswer);
      });
      setAnswerList(unfilteredAnswers.sort((a, b) => a.questionID - b.questionID));
    } else {
      setAnswerList(unfilteredAnswers);
    }
  };

  const onQuestionChange = (question: Question) => {
    setSelectedTender(null);
    setSelectedQuestion(question);
    
    const answers: TenderAnswer[] = [];
    tenders.forEach(t => {
      const qa = t.tenderAnswers?.find(ta => ta.questionID === question.questionID);
      if (qa) {
        answers.push({ ...qa, migrationID: qa.tenderID, tenderID: t.tenderID });
      } else {
        answers.push({
          migrationID: t.tenderID,
          tenderID: t.tenderID,
          questionID: question.questionID,
          value: null,
          type: convertQuestionType(question.questionType),
        });
      }
    });
    setQuestionAnswers(answers);
  };

  const getQuestion = (answer: TenderAnswer): Question => {
    if (!tendering) {
      return {
        text: 'Ingen fråga hittad i FFU...',
        heading: '',
        questionType: 'string',
        questionID: answer.questionID,
        tenderingPartID: 0,
      };
    }

    for (const part of tendering.tenderingParts) {
      const question = part.questions?.find(pq => pq.questionID === answer.questionID);
      if (question) return question;
    }

    return {
      text: 'Ingen fråga hittad i FFU...',
      heading: '',
      questionType: 'string',
      questionID: answer.questionID,
      tenderingPartID: 0,
    };
  };

  const getQuestionByID = (questionID: number): Question | undefined => {
    if (!tendering) return undefined;

    for (const part of tendering.tenderingParts) {
      const question = part.questions?.find(pq => pq.questionID === questionID);
      if (question) return question;
    }
    return undefined;
  };

  const getTenderName = (id: number): string => {
    const tender = tenders.find(t => t.tenderID === id);
    return tender?.tenderName || '';
  };

  const convertQuestionType = (type: string): string => {
    switch (type) {
      case 'Ja/Nej svar':
        return 'bool';
      case 'Fritextsvar':
        return 'string';
      case 'Fasta svarsalternativ':
        return 'bool';
      case 'Linjär skala':
        return 'decimalPriceLinear';
      case 'Prisruta':
        return 'decimalPrice';
      default:
        return 'string';
    }
  };

  const onAnswerEdit = (answer: TenderAnswer) => {
    if (!selectedTender) return;
    setSelectedAnswer(answer);
    setSelectedQuestionText(getQuestion(answer).text);
    setChangeAnswerOpen(true);
  };

  const onQuestionAnswerEdit = (answer: TenderAnswer) => {
    const tender = tenders.find(t => t.tenderID === answer.tenderID);
    if (!tender) return;
    
    setSelectedTender(tender);
    setSelectedAnswer(answer);
    setSelectedQuestionText(getQuestion(answer).text);
    setChangeAnswerOpen(true);
  };

  const handleAnswerSave = async (newAnswer: TenderAnswer) => {
    if (selectedTender && !selectedQuestion) {
      await getTendering(selectedTender);
    } else if (selectedQuestion) {
      setQuestionAnswers(questionAnswers.map(qa => 
        (qa.tenderID === newAnswer.migrationID || qa.tenderID === newAnswer.tenderID) ? newAnswer : qa
      ));
    }
    setChangeAnswerOpen(false);
  };

  const setHiddenProperty = async (hidden: boolean) => {
    if (!selectedTender) return;

    setConfirmDialogData({
      title: 'Ändra dold status?',
      text: `Vill du ${hidden ? 'dölja' : 'visa'} denna verksamhet?`,
      onConfirm: async () => {
        try {
          await changeTenderHideStatus(selectedTender.tenderID);
          setConfirmDialogOpen(false);
          // Refresh the tender data to show updated state
          await getTendering(selectedTender);
        } catch (error) {
          console.error(error);
          setErrorDialogOpen(true);
        }
      },
    });
    setConfirmDialogOpen(true);
  };

  const setTenderName = async (name: string) => {
    if (!selectedTender) return;

    try {
      const curr = await getTenderDocument(selectedTender.tenderID)
      curr.tenderName = name;
      await updateTenderDocument(selectedTender.tenderID, curr)
      setSuccessDialogOpen(true);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const deleteAnswer = (answer: TenderAnswer) => {
    if (!selectedTender) return;

    setConfirmDialogData({
      title: 'Radera svar?',
      text: 'Vill du radera detta svar?',
      onConfirm: async () => {
        try {
          const tender = tenders.find(t => t.tenderID === selectedTender.tenderID);
          if (tender && tender.tenderAnswers) {
            tender.tenderAnswers = tender.tenderAnswers.filter(a => a.questionID !== answer.questionID);
            await updateTenderDocument(tender.tenderID, tender)
            setAnswerList(answerList.map(a => 
              a.questionID === answer.questionID ? { ...a, value: null } : a
            ));
          }
          setConfirmDialogOpen(false);
        } catch (error) {
          console.error(error);
          setErrorDialogOpen(true);
        }
      },
    });
    setConfirmDialogOpen(true);
  };

  const onArchiveClick = () => {
    setPasswordDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!selectedTender) return;

    try {
      if (!selectedTender.archivingUsers) {
        const updatedTender = {
          ...selectedTender,
          archivingUsers: [user.user.userID],
        };
        await updateTenderDocument(selectedTender.tenderID, updatedTender);
        setPasswordDialogOpen(false);
      } else {
        setPasswordDialogOpen(false);
        setTenderLogOpen(true);
      }
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const handleTenderLogSave = async (note: NoteCreateDTO) => {
    if (!selectedTender) return;

    try {
      selectedTender.archivingUsers = selectedTender.archivingUsers || [];
      selectedTender.archivingUsers.push(user.user.userID);
      
      const updatedTender = {
        ...selectedTender,
        archivingUsers: selectedTender.archivingUsers,
      };

      await createNote(selectedTender.tenderID, note)
      await updateTenderDocument(selectedTender.tenderID, updatedTender);
      
      setTenderLogOpen(false);
      setSuccessDialogOpen(true);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  const disableButtonText = (): [boolean, string] => {
    if (!selectedTender?.archivingUsers) {
      return [false, 'Avpublicera verksamhet'];
    } else if (selectedTender.archivingUsers.includes(user.user.userID)) {
      return [true, 'Inväntar avpublicering'];
    } else {
      return [false, 'Bekräfta avpublicering'];
    }
  };

  const convertToNumber = (value: string): number | string => {
    return value ? Number(value) : '';
  };

  const convertBreakLines = (str: string): string => {
    const convertedString = str?.toString();
    return convertedString ? convertedString.replace(/\n/g, '<br>') : '';
  };

  const getAccountInfo = () => {
    if (!tendering) return;

    let query = '';
    tenders.forEach(t => {
      const name = getTenderAnswerValue(tendering.properties.questionMappings.contactName, t.tenderAnswers || []);
      const email = getTenderAnswerValue(tendering.properties.questionMappings.contactEmail, t.tenderAnswers || []);
      let phone = getTenderAnswerValue(tendering.properties.questionMappings.contactPhone, t.tenderAnswers || []);
      let domain = 'NULL';
      let firstName;
      let lastName;

      if (name) {
        firstName = name.split(' ')[0];
        lastName = name.split(' ')[1];
      }

      if (email) {
        domain = email.split('@')[1];
      }

      if (!phone) {
        phone = 'NULL';
      }

      query += `INSERT INTO [Tendering].[dbo].[ImportNew] ([TenderingID] ,[TenderID], [TenderName] ,[Domain] ,Email, [PhoneNumber], [Password], FirstName, LastName) VALUES (${tendering.tenderingID}, ${t.tenderID}, '${t.tenderName}', '${domain}', '${email}', '${phone}', '$2a$11$D6yd4J7M0fvGIjZlvCX/puwekbigptzYLMzqj26m1M/4nAhzYrEji', '${firstName}', '${lastName}');\n`;
    });
    console.log(query);
  };

  const getTenderAnswerValue = (qid: number, answers: TenderAnswer[]): any => {
    const found = answers.find(a => a.questionID === qid);
    return found ? found.value : '';
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="large" label="Laddar..." />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <h5 className="text-sm font-semibold mb-2">Välj Upphandling</h5>
          <Dropdown
            placeholder="Välj Upphandling"
            value={tenderingOptions.find(t => t.moduleID === selectedTenderingID)?.moduleName || ''}
            style={{ width: '100%', minWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            positioning={{autoSize: false}}
            listbox={{
              style: {
                maxHeight: '300px',
                overflowY: 'auto'
              },
            }}
            onOptionSelect={(_, data) => onTenderingChange(Number(data.optionValue))}
          >
            {tenderingOptions.map((option) => (
              <Option key={option.moduleID} value={option.moduleID.toString()}>
                {option.moduleName}
              </Option>
            ))}
          </Dropdown>
        </div>

        <div className="flex-1 min-w-[200px]">
          <h5 className="text-sm font-semibold mb-2">Specifik Fråga</h5>
          <Dropdown
            placeholder="Välj Fråga"
            value={selectedQuestion?.text || ''}
            style={{ width: '100%', minWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            positioning={{autoSize: false}}
            listbox={{
              style: {
                maxHeight: '300px',
                overflowY: 'auto'
              },
            }}
            onOptionSelect={(_, data) => {
              const question = questions.find(q => q.questionID === Number(data.optionValue));
              if (question) onQuestionChange(question);
            }}
          >
            {questions.map((q) => (
              <Option key={q.questionID} value={q.questionID.toString()}>
                {q.text}
              </Option>
            ))}
          </Dropdown>
        </div>

        <div className="flex-1 min-w-[200px]">
          <h5 className="text-sm font-semibold mb-2">Verksamhet</h5>
          <Dropdown
            placeholder="Välj Verksamhet"
            value={selectedTender?.tenderName || ''}
            style={{ width: '100%', minWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            positioning={{autoSize: false}}
            listbox={{
              style: {
                maxHeight: '300px',
                overflowY: 'auto'
              },
            }}
            onOptionSelect={(_, data) => {
              const tender = tenders.find(t => t.tenderID === Number(data.optionValue));
              if (tender) onTenderChange(tender);
            }}
          >
            {tenders.map((t) => (
              <Option key={t.tenderID} value={t.tenderID.toString()}>
                {t.tenderName}
              </Option>
            ))}
          </Dropdown>
        </div>

        {selectedTender && (!selectedTender.archivingUsers || selectedTender.archivingUsers.length < 2) && (
          <>
            <div className="flex-1 min-w-[200px]">
              <h5 className="text-sm font-semibold mb-2">Dold</h5>
              <RadioGroup
                value={selectedTender.tenderProperties?.hidden ? 'true' : 'false'}
                onChange={(_, data) => setHiddenProperty(data.value === 'true')}
                layout='horizontal'
              >
                <Radio value="true" label="Ja" />
                <Radio value="false" label="Nej" />
              </RadioGroup>
            </div>

            <Button
              appearance="primary"
              disabled={disableButtonText()[0]}
              onClick={onArchiveClick}
              className={disableButtonText()[0] ? '' : 'bg-red-600 hover:bg-red-700'}
            >
              {disableButtonText()[1]}
            </Button>
          </>
        )}
      </div>

      {selectedTender && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex gap-4 items-end mb-4">
            <div className="flex-1">
              <h5 className="text-sm font-semibold mb-2">Verksamhetens namn:</h5>
              <Input
                className="w-full"
                value={selectedTender.tenderName}
                onChange={(e) => setSelectedTender({ ...selectedTender, tenderName: e.target.value })}
              />
            </div>
            <Button appearance="primary" onClick={() => setTenderName(selectedTender.tenderName)}>
              Spara namn
            </Button>
          </div>
        </div>
      )}

      {selectedTender && (
        <div className="space-y-4">
          {answerList.map((answer) => (
            <div key={answer.questionID} className="bg-white rounded-lg shadow p-4 flex justify-between items-start">
              <div className="flex-1">
                <div className="font-semibold text-sm mb-1">{getQuestion(answer).heading}</div>
                <div className="text-sm text-gray-600 mb-2">
                  {getQuestion(answer).text} - {answer.questionID}
                </div>
                <div className="text-sm">
                  {answer.type === 'decimalPriceLinear' || answer.type === 'decimalPrice' ? (
                    convertToNumber(answer.value as string)
                  ) : answer.type === 'bool' ? (
                    answer.value === '1' ? 'Ja' : answer.value === '0' ? 'Nej' : ''
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: answer.value ? convertBreakLines(answer.value as string) : '' }} />
                  )}
                </div>
                {answer.comment && (
                  <div className="text-sm text-gray-600 mt-2">Kommentar: {answer.comment}</div>
                )}
              </div>
              <div className="flex gap-2 ml-4">
                <Button
                  appearance="subtle"
                  icon={<EditRegular />}
                  onClick={() => onAnswerEdit(answer)}
                  title="Ändra svar"
                />
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={() => deleteAnswer(answer)}
                  title="Radera svar"
                  className="text-red-600 hover:text-red-700"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedQuestion && (
        <div className="space-y-4">
          {questionAnswers.map((answer) => (
            <div key={`${answer.tenderID}-${answer.questionID}`} className="bg-white rounded-lg shadow p-4 flex justify-between items-start">
              <div className="flex-1">
                <div className="font-semibold text-sm mb-1 text-blue-600">{getTenderName(answer.tenderID)}</div>
                <div className="font-semibold text-sm mb-1">{getQuestion(answer).heading}</div>
                <div className="text-sm text-gray-600 mb-2">
                  {getQuestion(answer).text} - {answer.questionID}
                </div>
                <div className="text-sm">
                  {answer.type === 'decimalPriceLinear' || answer.type === 'decimalPrice' ? (
                    convertToNumber(answer.value as string)
                  ) : answer.type === 'bool' ? (
                    answer.value === '1' ? 'Ja' : answer.value === '0' ? 'Nej' : ''
                  ) : (
                    answer.value
                  )}
                </div>
                {answer.comment && (
                  <div className="text-sm text-gray-600 mt-2">Kommentar: {answer.comment}</div>
                )}
              </div>
              <div className="flex gap-2 ml-4">
                <Button
                  appearance="subtle"
                  icon={<EditRegular />}
                  onClick={() => onQuestionAnswerEdit(answer)}
                  title="Ändra svar"
                />
                {selectedTender && (
                  <Button
                    appearance="subtle"
                    icon={<DeleteRegular />}
                    onClick={() => deleteAnswer(answer)}
                    title="Radera svar"
                    className="text-red-600 hover:text-red-700"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {user.user.userID === 1 && (
        <Button appearance="primary" onClick={getAccountInfo}>
          + SKAPA KONTOINFO
        </Button>
      )}

      {/* Dialogs */}
      {changeAnswerOpen && selectedAnswer && selectedTender && (
        <ChangeAnswerDialog
          isOpen={changeAnswerOpen}
          onClose={() => setChangeAnswerOpen(false)}
          onSave={handleAnswerSave}
          tender={selectedTender}
          question={selectedQuestionText}
          answer={selectedAnswer}
          user={user.user}
        />
      )}

      {passwordDialogOpen && selectedTender && (
        <PasswordDialog
          isOpen={passwordDialogOpen}
          onClose={() => setPasswordDialogOpen(false)}
          onConfirm={handleArchiveConfirm}
          title={`Vill du avpublicera ${selectedTender.tenderName}?`}
          user={user.user}
          presetPassword={systemPassword}
        />
      )}

      {confirmDialogOpen && confirmDialogData && (
        <ConfirmationDialog
          isOpen={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={confirmDialogData.onConfirm}
          title={confirmDialogData.title}
          text={confirmDialogData.text}
        />
      )}

      {successDialogOpen && (
        <SuccessDialog isOpen={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} />
      )}

      {errorDialogOpen && (
        <ErrorDialog isOpen={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} />
      )}

      {tenderLogOpen && selectedTender && (
        <NewNote
          open={tenderLogOpen}
          onClose={() => setTenderLogOpen(false)}
          onSave={handleTenderLogSave}
          initialData={{
            noteHeader: `${selectedTender.tenderName} avpublicering`,
            noteSubject: 'Verksamhet avpublicering',
          }}
        />
      )}
    </div>
  );
};

export default SystemTenders;
